import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { X, Download, Calendar, Filter } from 'lucide-react';
import { Device } from '../types';
import exportService, { MetricDefinition, ExportMetricsRequest } from '../services/exportService';

/**
 * Animation keyframes
 */
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const scaleIn = keyframes`
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  devices: Device[];
}

/**
 * Grouped metrics by device
 */
interface GroupedMetrics {
  deviceName: string;
  deviceId: string;
  metrics: MetricDefinition[];
}

/**
 * Default number of days for date range
 */
const DEFAULT_DATE_RANGE_DAYS = 30;

/**
 * ExportModal Component
 * Modal for exporting device metrics data to CSV format
 */
const ExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose, devices }) => {
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const [groupedMetrics, setGroupedMetrics] = useState<GroupedMetrics[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectAllMetrics, setSelectAllMetrics] = useState(false);

  /**
   * Flatten devices to include both stations and their modules
   */
  const flattenedDevices = React.useMemo(() => {
    const result: Device[] = [];

    devices.forEach(device => {
      // Add the main device (station or standalone device)
      result.push(device);

      // If it's a station with modules, add each module
      if (device.isStation && device.modules) {
        device.modules.forEach(module => {
          result.push(module);
        });
      }
    });

    return result;
  }, [devices]);

  /**
   * Initialize date range and load metrics when modal opens or devices change
   */
  useEffect(() => {
    if (isOpen) {
      initializeDateRange();
      if (selectedDeviceIds.length > 0) {
        loadMetricsForSelectedDevices();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedDeviceIds]);

  /**
   * Load metrics for selected devices
   */
  const loadMetricsForSelectedDevices = async (): Promise<void> => {
    if (selectedDeviceIds.length === 0) {
      setGroupedMetrics([]);
      return;
    }

    try {
      setIsLoadingMetrics(true);

      // Fetch latest data for each selected device to get their available metrics
      const metricsPromises = selectedDeviceIds.map(async (deviceId) => {
        const device = flattenedDevices.find(d => d.id === deviceId);
        if (!device) return null;

        try {
          const response = await fetch(`/api/devices/${deviceId}/latest`);
          if (!response.ok) return null;

          const data = await response.json();

          // Get all available metrics from the device
          const allMetrics = await exportService.getAvailableMetrics();

          // Filter metrics that exist in this device's data
          const deviceMetrics = allMetrics.filter(metric =>
            data.data && metric.key in data.data
          );

          return {
            deviceName: device.name || device.deviceId,
            deviceId: device.id,
            metrics: deviceMetrics
          };
        } catch {
          return null;
        }
      });

      const results = await Promise.all(metricsPromises);
      const validResults = results.filter((r): r is GroupedMetrics => r !== null);
      setGroupedMetrics(validResults);
    } catch (err) {
      console.error('Failed to load metrics for devices:', err);
    } finally {
      setIsLoadingMetrics(false);
    }
  };

  /**
   * Initialize date range to last 30 days
   */
  const initializeDateRange = (): void => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - DEFAULT_DATE_RANGE_DAYS);
    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
  };

  /**
   * Toggle device selection
   */
  const handleDeviceToggle = (deviceId: string): void => {
    setSelectedDeviceIds(prev =>
      prev.includes(deviceId)
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
    // Reset metric selection when devices change
    setSelectedMetrics([]);
    setSelectAllMetrics(false);
  };

  /**
   * Toggle metric selection
   */
  const handleMetricToggle = (metricKey: string): void => {
    setSelectedMetrics(prev =>
      prev.includes(metricKey)
        ? prev.filter(key => key !== metricKey)
        : [...prev, metricKey]
    );
  };

  /**
   * Toggle all metrics selection
   */
  const handleSelectAllMetrics = (): void => {
    if (selectAllMetrics) {
      setSelectedMetrics([]);
    } else {
      // Get all unique metrics from all selected devices
      const allMetricKeys = new Set<string>();
      groupedMetrics.forEach(group => {
        group.metrics.forEach(metric => allMetricKeys.add(metric.key));
      });
      setSelectedMetrics(Array.from(allMetricKeys));
    }
    setSelectAllMetrics(!selectAllMetrics);
  };

  /**
   * Toggle all devices selection
   */
  const handleSelectAllDevices = (): void => {
    if (selectedDeviceIds.length === flattenedDevices.length) {
      setSelectedDeviceIds([]);
    } else {
      setSelectedDeviceIds(flattenedDevices.map(d => d.id));
    }
  };

  /**
   * Validate and trigger export
   */
  const handleExport = async (): Promise<void> => {
    setError(null);

    // Validation
    if (selectedDeviceIds.length === 0) {
      setError('Please select at least one device');
      return;
    }

    if (!startDate || !endDate) {
      setError('Please select start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before end date');
      return;
    }

    try {
      setIsExporting(true);
      const request: ExportMetricsRequest = {
        deviceIds: selectedDeviceIds,
        metrics: selectedMetrics, // Empty array means all metrics
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(endDate).toISOString(),
      };
      await exportService.exportMetrics(request);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <Download size={24} />
            Export Metrics Data
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          {error && <ErrorMessage>{error}</ErrorMessage>}

          {/* Date Range Section */}
          <Section>
            <SectionTitle>
              <Calendar size={20} />
              Date Range
            </SectionTitle>
            <DateRangeContainer>
              <DateInputGroup>
                <Label>Start Date</Label>
                <DateInput
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </DateInputGroup>
              <DateInputGroup>
                <Label>End Date</Label>
                <DateInput
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </DateInputGroup>
            </DateRangeContainer>
          </Section>

          {/* Device Selection Section */}
          <Section>
            <SectionHeader>
              <SectionTitle>
                <Filter size={20} />
                Select Devices ({selectedDeviceIds.length} selected)
              </SectionTitle>
              <SelectAllButton onClick={handleSelectAllDevices}>
                {selectedDeviceIds.length === flattenedDevices.length ? 'Deselect All' : 'Select All'}
              </SelectAllButton>
            </SectionHeader>
            <CheckboxList>
              {flattenedDevices.map((device) => (
                <CheckboxItem key={device.id} onClick={() => handleDeviceToggle(device.id)} style={{cursor: 'pointer'}}>
                  <Checkbox
                    type="checkbox"
                    checked={selectedDeviceIds.includes(device.id)}
                    onChange={() => handleDeviceToggle(device.id)}
                    onClick={e => e.stopPropagation()} // Prevent double toggle
                  />
                  <CheckboxLabel>
                    {device.name || device.deviceId}
                    <DeviceTypeText>({device.deviceType})</DeviceTypeText>
                  </CheckboxLabel>
                </CheckboxItem>
              ))}
            </CheckboxList>
          </Section>

          {/* Metrics Selection Section - Grouped by Device */}
          <AnimatedSection expanded={selectedDeviceIds.length > 0}>
            {selectedDeviceIds.length > 0 && (
              <Section>
                <SectionHeader>
                  <SectionTitle>
                    <Filter size={20} />
                    Select Metrics ({selectedMetrics.length > 0 ? `${selectedMetrics.length} selected` : 'All metrics'})
                  </SectionTitle>
                  <SelectAllButton onClick={handleSelectAllMetrics}>
                    {selectAllMetrics ? 'Deselect All' : 'Select All'}
                  </SelectAllButton>
                </SectionHeader>
                <MetricsNote>Leave empty to export all available metrics for selected devices</MetricsNote>

                {isLoadingMetrics ? (
                  <LoadingText>Loading metrics...</LoadingText>
                ) : groupedMetrics.length > 0 ? (
                  <MetricsGroupContainer>
                    {groupedMetrics.map((group) => (
                      <DeviceMetricsGroup key={group.deviceId}>
                        <DeviceGroupHeader>{group.deviceName}</DeviceGroupHeader>
                        <CheckboxList>
                          {group.metrics.map((metric) => (
                            <CheckboxItem key={`${group.deviceId}-${metric.key}`} onClick={() => handleMetricToggle(metric.key)} style={{cursor: 'pointer'}}>
                              <Checkbox
                                type="checkbox"
                                checked={selectedMetrics.includes(metric.key)}
                                onChange={() => handleMetricToggle(metric.key)}
                                onClick={e => e.stopPropagation()} // Prevent double toggle
                              />
                              <CheckboxLabel>
                                {metric.label}
                                <MetricUnitText>({metric.unit})</MetricUnitText>
                              </CheckboxLabel>
                            </CheckboxItem>
                          ))}
                        </CheckboxList>
                      </DeviceMetricsGroup>
                    ))}
                  </MetricsGroupContainer>
                ) : (
                  <NoMetricsText>No metrics available for selected devices</NoMetricsText>
                )}
              </Section>
            )}
          </AnimatedSection>
        </ModalContent>

        <ModalFooter>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <ExportButton onClick={handleExport} disabled={isExporting}>
            {isExporting ? 'Exporting...' : 'Export to CSV'}
          </ExportButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ExportModal;

// Styled Components

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease-out;
`;

const ModalContainer = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #1f1f3a 100%);
  border-radius: 16px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.1);
  animation: ${slideUp} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(180deg, rgba(0, 212, 255, 0.05) 0%, transparent 100%);
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #ffffff;
  font-size: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  
  svg {
    color: #00d4ff;
    animation: ${scaleIn} 0.5s ease-out 0.2s both;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border-radius: 8px;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
    transform: rotate(90deg);
  }

  &:active {
    transform: rotate(90deg) scale(0.9);
  }
`;

const ModalContent = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 212, 255, 0.3);
    border-radius: 4px;
    transition: background 0.3s ease;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 212, 255, 0.5);
  }
`;

const Section = styled.div`
  margin-bottom: 32px;
  animation: ${fadeIn} 0.5s ease-out;
  animation-fill-mode: both;

  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  color: #ffffff;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 10px;
  
  svg {
    color: #00d4ff;
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: scale(1.1) rotate(5deg);
  }
`;

const SelectAllButton = styled.button`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  color: #00d4ff;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: rgba(0, 212, 255, 0.2);
    border-color: rgba(0, 212, 255, 0.5);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 212, 255, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const DateRangeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const DateInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: #b0b0b0;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: color 0.3s ease;
`;

const DateInput = styled.input`
  background: #2d2d44;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 12px;
  color: #ffffff;
  font-size: 14px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.2);
    background: #363650;
  }

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const CheckboxList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
  padding: 16px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 212, 255, 0.3);
    border-radius: 3px;
  }
`;

const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: rgba(0, 212, 255, 0.1);
    transform: translateX(4px);
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #00d4ff;
  transition: transform 0.2s ease;

  &:checked {
    transform: scale(1.1);
  }
`;

const CheckboxLabel = styled.label`
  color: #e0e0e0;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  transition: color 0.2s ease;

  ${CheckboxItem}:hover & {
    color: #fff;
  }
`;

const DeviceTypeText = styled.span`
  color: #888;
  font-size: 12px;
  transition: color 0.2s ease;

  ${CheckboxItem}:hover & {
    color: #00d4ff;
  }
`;

const MetricUnitText = styled.span`
  color: #00d4ff;
  font-size: 12px;
  font-weight: 500;
`;

const MetricsNote = styled.p`
  color: #888;
  font-size: 13px;
  margin: -8px 0 12px 0;
  font-style: italic;
  padding: 8px 12px;
  background: rgba(0, 212, 255, 0.05);
  border-left: 3px solid rgba(0, 212, 255, 0.3);
  border-radius: 4px;
`;

const ErrorMessage = styled.div`
  background: linear-gradient(135deg, #ff4444, #cc0000);
  color: white;
  padding: 14px 16px;
  border-radius: 10px;
  margin-bottom: 16px;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(255, 68, 68, 0.4);
  border-left: 4px solid #ff0000;
  animation: ${slideUp} 0.3s ease-out;
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: '⚠';
    font-size: 20px;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.2) 100%);
`;

const CancelButton = styled.button`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #ffffff;
  padding: 12px 24px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ExportButton = styled.button`
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  border: none;
  color: #ffffff;
  padding: 12px 28px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0, 212, 255, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.5s ease;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 212, 255, 0.5);
    background: linear-gradient(135deg, #00e0ff, #00a8dd);
  }

  &:hover:not(:disabled)::before {
    left: 100%;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #555;
    
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 20px;
      height: 20px;
      margin: -10px 0 0 -10px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: #fff;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const MetricsGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 400px;
  overflow-y: auto;
  padding: 16px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 212, 255, 0.3);
    border-radius: 3px;
  }
`;

const DeviceMetricsGroup = styled.div`
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 10px;
  padding: 16px;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.05) 0%, rgba(0, 153, 204, 0.05) 100%);
  transition: all 0.3s ease;
  animation: ${scaleIn} 0.4s ease-out;

  &:hover {
    border-color: rgba(0, 212, 255, 0.4);
    box-shadow: 0 4px 16px rgba(0, 212, 255, 0.15);
    transform: translateY(-2px);
  }
`;

const DeviceGroupHeader = styled.h4`
  margin: 0 0 12px 0;
  color: #00d4ff;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '▸';
    transition: transform 0.3s ease;
  }

  ${DeviceMetricsGroup}:hover &::before {
    transform: translateX(4px);
  }
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 40px;
  color: #888;
  font-size: 14px;
  
  &::after {
    content: '...';
    animation: ellipsis 1.5s infinite;
  }

  @keyframes ellipsis {
    0%, 20% { content: '.'; }
    40% { content: '..'; }
    60%, 100% { content: '...'; }
  }
`;

const NoMetricsText = styled.div`
  text-align: center;
  padding: 40px;
  color: #888;
  font-size: 14px;
  font-style: italic;
`;

const AnimatedSection = styled.div<{ expanded: boolean }>`
  overflow: hidden;
  max-height: ${({ expanded }) => (expanded ? '1000px' : '0')};
  opacity: ${({ expanded }) => (expanded ? 1 : 0)};
  transform: ${({ expanded }) => (expanded ? 'translateY(0)' : 'translateY(-20px)')};
  transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;
